'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';

import {
  Info,
  PhoneCall,
  Lock,
  MapPin,
  Cpu,
  ArrowLeft,
  ChevronRight,
} from 'lucide-react';
import Topbar from '@/components/Topbar';

const INFO_CARDS = [
  {
    icon: Info,
    label: 'Not a Diagnostic Tool',
    body: 'ATLAS is a care navigation aid only. It does not diagnose medical conditions or replace the assessment of a qualified healthcare professional.',
  },
  {
    icon: PhoneCall,
    label: 'For Emergencies Call 9-1-1',
    body: 'If you or someone else is in immediate danger, do not use this tool. Call 911 now.',
  },
  {
    icon: Lock,
    label: 'Anonymous and Private',
    body: 'This assessment is fully anonymous. No personal information is collected, stored, or shared.',
  },
  {
    icon: MapPin,
    label: 'Nova Scotia Health System',
    body: 'ATLAS is designed for residents of Nova Scotia. All care recommendations and facility information are based on NS health services.',
  },
  {
    icon: Cpu,
    label: 'AI-Assisted Care Routing',
    body: 'Your responses are processed using a CEDIS-informed scoring model to suggest the most appropriate level of care for your situation.',
  },
];

export default function AssessmentStep1Client() {
  const router = useRouter();
  const [confirmed, setConfirmed] = useState(false);

  return (
    <div className="min-h-screen bg-[#f0f4fc] flex flex-col">
      <Topbar />
      <main className="flex-1 py-8 px-6 lg:px-10 xl:px-16">
        <div className="max-w-2xl mx-auto">

          {/* Progress bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-[#1a2744]">Step 1 of 4</p>
              <p className="text-sm text-[#6b7a99]">Personal Information</p>
            </div>
            <div className="w-full h-2 bg-[#e4eaf5] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#7ebc59] rounded-full transition-all duration-500"
                style={{ width: '25%' }}
              />
            </div>
            <div className="flex justify-between mt-2">
              {['Overview', 'Symptoms', 'Additional Detail', 'Results']?.map((label, i) => (
                <div key={`step-label-${i}`} className="flex flex-col items-center">
                  <div
                    className={`w-2 h-2 rounded-full mb-1 ${
                      i === 0 ? 'bg-[#7ebc59]' : 'bg-[#d1daea]'
                    }`}
                  />
                  <span
                    className={`text-xs font-medium hidden sm:block ${
                      i === 0 ? 'text-[#7ebc59]' : 'text-[#6b7a99]'
                    }`}
                  >
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Card */}
          <div className="bg-white rounded-3xl shadow-card border border-[#e4eaf5] p-8">
            <Link
              href="/dashboard"
              className="flex items-center gap-1.5 text-[#6b7a99] hover:text-[#204998] text-sm mb-6 transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Dashboard
            </Link>

            <h1 className="text-2xl font-bold text-[#1a2744] mb-1">
              Before You Begin
            </h1>
            <p className="text-[#6b7a99] text-sm mb-7">
              Please read the following before starting your assessment.
            </p>

            <div className="space-y-3 mb-7">
              {INFO_CARDS.map((item) => (
                <div
                  key={`info-${item.label}`}
                  className="flex items-center gap-4 p-4 bg-[#f8faff] rounded-xl border border-[#e4eaf5]"
                >
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <item.icon size={18} className="text-[#204998]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[#6b7a99] font-medium">{item.label}</p>
                    <p className="text-sm text-[#1a2744] font-semibold">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Confirmation checkbox */}
            <label
              htmlFor="confirm-check"
              className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-150 ${
                confirmed
                  ? 'border-[#7ebc59] bg-[#f2f9ec]'
                  : 'border-[#e4eaf5] bg-[#f8faff] hover:border-[#204998]/30'
              }`}
            >
              <input
                type="checkbox"
                id="confirm-check"
                checked={confirmed}
                onChange={(e) => setConfirmed(e?.target?.checked)}
                className="mt-0.5 w-5 h-5 rounded accent-[#7ebc59] cursor-pointer flex-shrink-0"
              />
              <div>
                <p className="text-sm font-semibold text-[#1a2744]">
                  I understand that ATLAS is a navigation tool only and is not a substitute for professional medical advice.
                </p>
              </div>
            </label>

            <button
              onClick={() => router?.push('/assessment-symptom-intake')}
              disabled={!confirmed}
              className="btn-primary w-full flex items-center justify-center gap-2 text-base py-4 mt-5"
            >
              Continue to Symptom Assessment
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}